import { userActions } from 'redux/user/userSlice'
import Loading from 'core-system/Loading'
import React, { useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useNavigate } from 'react-router-dom'
import { AppState } from 'redux/config/store'
import { Employee } from 'redux/employee/employeeTypes'
import {
  Employer as EmployerProfile,
  Worksite,
} from 'redux/employer/employerTypes'
import { User } from 'redux/user/userTypes'
import { Locations } from 'shared/Router/Locations'
import styled from 'styled-components'
import BillingView from './Payments/BillingView'
import UsersView from './Users/UsersView'
import Tabs from 'core-system/Tabs/Tabs'
import PrimaryButton from 'core-system/Button/PrimaryButton'

const Container = styled.div`
  margin: 2rem;
`

const InnerContainer = styled.div`
  margin: 2rem 0rem;
`

const renderContent = (
  activePage: number,
  allEmployees: Employee[],
  employerInfo: {
    profile: EmployerProfile
    allWorksites: Worksite[]
  },
  user: User
) => {
  switch (activePage) {
    case 0: {
      //account for users without a worksite
      const noWorksite = {
        id: 'no-worksite',
        alias: 'No Worksite',
        address: '',
        employeeCount: null,
        employeeArchivedCount: null,
        contractorCount: null,
        formattedAddress: '',
        city: '',
        iconId: null,
        physical: false,
        shipped: null,
        cardsActivated: null,
      }

      return (
        <UsersView
          allEmployees={allEmployees}
          allWorksites={[...employerInfo.allWorksites, noWorksite]}
          user={{ ...user }}
        />
      )
    }
    case 1:
      return <BillingView />
    default:
      return null
  }
}

const AccountsView = React.memo(() => {
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const [activePage, setActivePage] = useState(0)
  const user = useSelector((state: AppState) => state.user.profile)
  const allPrograms = useSelector((state: AppState) => state.programs.programs)
  const allEmployees = useSelector(
    (state: AppState) => state.employee.list,
    shallowEqual
  )
  const employerInfo = useSelector((state: AppState) => {
    return {
      profile: state.employer.profile,
      allWorksites: state.employer.worksites,
    }
  })

  // Only for initial load
  useEffect(() => {
    if (location.pathname === Locations.Accounts.Profile) {
      setActivePage(0)
    } else if (location.pathname === Locations.Accounts.Billing) {
      setActivePage(1)
    }
  }, [])

  const tabs = [
    { label: 'Account', value: 0, location: Locations.Accounts.Profile },
    { label: 'Billing', value: 1, location: Locations.Accounts.Billing },
  ]

  return (
    <Container>
      <Tabs
        value={activePage}
        onChange={(_, value) => {
          setActivePage(value)
          navigate(`${tabs[value].location}`)
        }}
        aria-label='accounts page tabs'
        tabs={tabs}
      />
      <InnerContainer>
        {user && employerInfo && allEmployees && allPrograms ? (
          renderContent(activePage, allEmployees, employerInfo, user)
        ) : (
          <Loading isCard={false} height='100%' />
        )}
      </InnerContainer>
      <PrimaryButton onClick={() => dispatch(userActions.logout())}>
        Logout
      </PrimaryButton>
    </Container>
  )
})

// Helps to identify component in React error logs
if (process.env.NODE_ENV !== 'production') {
  AccountsView.displayName = 'AccountsView'
}

export default AccountsView
